import { cva, type VariantProps } from 'class-variance-authority';
import { type ComponentProps } from 'react';

import { cn } from '@ui/lib/utils';

enum AlertVariant {
  DEFAULT = 'default',
  DESTRUCTIVE = 'destructive',
  WARNING = 'warning',
  INFO = 'info',
}

const alertBase = [
  'group/alert relative grid w-full gap-0.5 rounded-lg border px-2.5 py-2 text-left text-sm',
  'has-data-[slot=alert-action]:relative has-data-[slot=alert-action]:pr-18',
  'has-[>svg]:grid-cols-[auto_1fr] has-[>svg]:gap-x-2',
  '*:[svg]:row-span-2 *:[svg]:translate-y-0.5 *:[svg]:text-current',
  "*:[svg:not([class*='size-'])]:size-4",
].join(' ');

const alertVariants = cva(alertBase, {
  variants: {
    variant: {
      [AlertVariant.DEFAULT]: 'border-fg-faint bg-card text-card-foreground',
      [AlertVariant.DESTRUCTIVE]:
        'bg-card text-destructive *:data-[slot=alert-description]:text-destructive/90 *:[svg]:text-current',
      [AlertVariant.WARNING]: [
        'border-warn/35 bg-warn-soft text-foreground',
        '*:data-[slot=alert-description]:text-foreground/80 *:[svg]:text-warn',
      ].join(' '),
      [AlertVariant.INFO]: [
        'border-info/35 bg-info-soft text-foreground',
        '*:data-[slot=alert-description]:text-foreground/80 *:[svg]:text-info-deep',
      ].join(' '),
    },
  },
  defaultVariants: {
    variant: AlertVariant.DEFAULT,
  },
});

type AlertProps = ComponentProps<'div'> &
  Omit<VariantProps<typeof alertVariants>, 'variant'> & { variant?: AlertVariant };

const Alert = ({ className, variant, ...props }: AlertProps) => (
  <div data-slot="alert" role="alert" className={cn(alertVariants({ variant }), className)} {...props} />
);

const AlertTitle = ({ className, ...props }: ComponentProps<'div'>) => (
  <div
    data-slot="alert-title"
    className={cn(
      'font-medium group-has-[>svg]/alert:col-start-2 [&_a]:underline [&_a]:underline-offset-3',
      '[&_a]:hover:text-foreground',
      className
    )}
    {...props}
  />
);

const AlertDescription = ({ className, ...props }: ComponentProps<'div'>) => (
  <div
    data-slot="alert-description"
    className={cn(
      'text-sm text-muted-foreground [&_a]:underline [&_a]:underline-offset-3',
      '[&_a]:hover:text-foreground [&_p:not(:last-child)]:mb-4',
      className
    )}
    {...props}
  />
);

const AlertAction = ({ className, ...props }: ComponentProps<'div'>) => (
  <div data-slot="alert-action" className={cn('absolute top-2 right-2', className)} {...props} />
);

export { Alert, AlertTitle, AlertDescription, AlertAction, AlertVariant };
